import { type ChangeEvent, type ComponentType, type CSSProperties, type RefObject, useMemo } from 'react';
import { type DateValue, fromDate, parseAbsolute, parseDate } from '@internationalized/date';
import type { DatePickerAria } from 'react-aria';
import { match, P } from 'ts-pattern';
import { usePreferredTimeZone } from '../../../time-zone-context';
import { useCurrentDate } from '../../../util/date';

export type DatePickerPopoverSize = 'match';

export interface DatePickerPagination<T = any> {
  lastAvailableDateDirection?: 'before' | 'after';
  lastAvailableDate?: Date | string | null;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => Promise<T>;
}

export type DatePickerActivatorRenderer = ComponentType<DatePickerAria>;

export function getDatePickerCalendarStyles(triggerRef: RefObject<HTMLElement | null>, size: DatePickerPopoverSize | undefined): CSSProperties {
  if (!size || !triggerRef.current) {
    return {};
  }

  return {
    '--ixf-calendar-width': size === 'match' ? `${triggerRef.current.offsetWidth}px` : undefined,
  } as CSSProperties;
}

export function getDateValueFromValue(value: Date | string | null | undefined, allowTime: boolean | undefined, timezone: string): DateValue | null {
  try {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'string') {
      return allowTime ? parseAbsolute(value, timezone) : parseDate(value.split('T')[0]);
    }

    return fromDate(value, timezone);
  } catch {
    return null;
  }
}

export function buildChangeEvent(name: string, value: DateValue | null, timezone: string, allowTime: boolean = false): ChangeEvent<HTMLInputElement> {
  const strValue = match(value)
    .with({ toAbsoluteString: P.not(P.nullish) }, (v) => v.toAbsoluteString())
    .with({ toString: P.not(P.nullish) }, (v) => v.toString())
    .otherwise(() => undefined);

  const valueAsDate = value?.toDate(timezone);

  return {
    target: {
      name,
      value: allowTime ? valueAsDate?.toISOString() : strValue,
      valueAsDate,
    },
  } as ChangeEvent<HTMLInputElement>;
}

export type DateAvailabilityDeterminer = (date: DateValue) => boolean;

export function getDateAvailabilityDeterminerNotBefore(notBeforeDate: Date, timeZone: string): DateAvailabilityDeterminer {
  return (date: DateValue) => date.compare(fromDate(notBeforeDate, timeZone)) <= 0;
}

export function getDateAvailabilityDeterminerNotAfter(notAfterDate: Date, timeZone: string): DateAvailabilityDeterminer {
  return (date: DateValue) => date.compare(fromDate(notAfterDate, timeZone)) >= 0;
}

export function useDateAfterTodayDeterminer(timeZone?: string): DateAvailabilityDeterminer {
  const preferredTimeZone = usePreferredTimeZone();
  const usedTimeZone = timeZone ?? preferredTimeZone;
  const { date: today } = useCurrentDate(usedTimeZone);

  return useMemo(() => getDateAvailabilityDeterminerNotAfter(today.add({ days: 1 }).toDate(usedTimeZone), usedTimeZone), [usedTimeZone, today]);
}
